import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../lib/utils';

export type MetricTrend = 'up' | 'down' | 'neutral';

export interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: MetricTrend;
  trendValue?: string;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const trendConfig: Record<MetricTrend, { icon: React.ElementType; color: string; bg: string }> = {
  up: {
    icon: TrendingUp,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
  down: {
    icon: TrendingDown,
    color: 'text-red-600',
    bg: 'bg-red-50',
  },
  neutral: {
    icon: Minus,
    color: 'text-slate-500',
    bg: 'bg-slate-100',
  },
};

export function MetricCard({
  label,
  value,
  trend,
  trendValue,
  icon,
  className,
  onClick,
}: MetricCardProps) {
  const trendInfo = trend ? trendConfig[trend] : null;

  return (
    <div
      className={cn(
        'relative min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-interactive duration-200 ease-out',
        onClick && 'cursor-pointer hover:border-statura-green-accessible/30 hover:shadow-md',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <p className="min-w-0 break-words font-body text-eyebrow uppercase text-text-muted">
            {label}
          </p>
          <p className="min-w-0 break-words font-body text-metric text-text-heading">{value}</p>
        </div>
        {icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-statura-green-accessible/15 bg-statura-green-accessible/10 text-statura-green-accessible [&_svg]:h-4 [&_svg]:w-4">
            {icon}
          </div>
        )}
      </div>

      {trendInfo && trendValue && (
        <div className="mt-4 flex min-w-0 flex-wrap items-center gap-1.5">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm',
              trendInfo.bg,
              trendInfo.color,
            )}
          >
            <trendInfo.icon className="h-3 w-3" />
            {trendValue}
          </span>
        </div>
      )}
    </div>
  );
}
